import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isPhysicalMobileGameViewport } from '../utils/isPhysicalMobileGameViewport';

/**
 * Custom hook for viewport-based scaling
 * Calculates a scale factor based on current viewport vs base resolution
 * and provides CSS variables for consistent scaling across all elements
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.baseWidth - Base design width (default: 1920)
 * @param {number} options.baseHeight - Base design height (default: 1080)
 * @param {number} options.minScale - Minimum scale factor (default: 0.5)
 * @param {number} options.maxScale - Maximum scale factor (default: 1.0) - capped at 1 to prevent upscaling beyond base resolution
 * @param {boolean} options.scaleByWidth - Scale based on width (default: true)
 * @param {boolean} options.scaleByHeight - Scale based on height (default: true)
 * @returns {Object} - { scale, scaleX, scaleY, baseWidth, baseHeight, viewportWidth, viewportHeight }
 */
export function useViewportScale(options = {}) {
  const {
    baseWidth = 1920,
    baseHeight = 1080,
    minScale = 0.5,
    maxScale = 1.0, // Default to 1.0 - no upscaling beyond base resolution
    scaleByWidth = true,
    scaleByHeight = true
  } = options;

  const [scale, setScale] = useState(1);
  const [scaleX, setScaleX] = useState(1);
  const [scaleY, setScaleY] = useState(1);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  useEffect(() => {
    const updateScale = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      setViewportWidth(width);
      setViewportHeight(height);

      // Calculate scale factors for both dimensions
      const calculatedScaleX = width / baseWidth;
      const calculatedScaleY = height / baseHeight;

      // Determine final scale based on options
      let finalScale = 1;
      
      if (scaleByWidth && scaleByHeight) {
        // Use the smaller scale to ensure everything fits (maintains aspect ratio)
        finalScale = Math.min(calculatedScaleX, calculatedScaleY);
      } else if (scaleByWidth) {
        finalScale = calculatedScaleX;
      } else if (scaleByHeight) {
        finalScale = calculatedScaleY;
      }

      // Clamp scale to min/max bounds
      finalScale = Math.max(minScale, Math.min(maxScale, finalScale));

      setScale(finalScale);
      setScaleX(calculatedScaleX);
      setScaleY(calculatedScaleY);
    };

    // Initial calculation
    updateScale();

    // Update on resize
    window.addEventListener('resize', updateScale);
    
    // Also listen for orientation changes
    window.addEventListener('orientationchange', updateScale);

    return () => {
      window.removeEventListener('resize', updateScale);
      window.removeEventListener('orientationchange', updateScale);
    };
  }, [baseWidth, baseHeight, minScale, maxScale, scaleByWidth, scaleByHeight]);

  return {
    scale,
    scaleX,
    scaleY,
    baseWidth,
    baseHeight,
    viewportWidth,
    viewportHeight
  };
}

/**
 * Higher-order component or hook that applies viewport scaling via CSS custom properties
 * This allows all CSS to use relative units that scale automatically
 */
export function useViewportScaleCSS(options = {}) {
  const scaleData = useViewportScale(options);

  useEffect(() => {
    const root = document.documentElement;
    
    // Set CSS custom properties for scaling
    root.style.setProperty('--viewport-scale', scaleData.scale);
    root.style.setProperty('--viewport-scale-x', scaleData.scaleX);
    root.style.setProperty('--viewport-scale-y', scaleData.scaleY);
    root.style.setProperty('--base-width', `${scaleData.baseWidth}px`);
    root.style.setProperty('--base-height', `${scaleData.baseHeight}px`);
    
    // Calculate scaled dimensions
    root.style.setProperty('--scaled-width', `${scaleData.viewportWidth}px`);
    root.style.setProperty('--scaled-height', `${scaleData.viewportHeight}px`);
    
    // Helper: scaled pixel values (e.g., 100px base becomes 100 * scale)
    root.style.setProperty('--px', `${scaleData.scale}px`);
    
    return () => {
      // Cleanup (optional, but good practice)
      root.style.removeProperty('--viewport-scale');
      root.style.removeProperty('--viewport-scale-x');
      root.style.removeProperty('--viewport-scale-y');
      root.style.removeProperty('--base-width');
      root.style.removeProperty('--base-height');
      root.style.removeProperty('--scaled-width');
      root.style.removeProperty('--scaled-height');
      root.style.removeProperty('--px');
    };
  }, [scaleData]);

  return scaleData;
}

/**
 * Hook specifically designed for container-based viewport scaling.
 * This hook is used to wrap the entire game UI in a fixed-size container
 * that scales uniformly to fit the viewport.
 * 
 * Key features:
 * - DESKTOP: Fixed base size of 1920x1080 pixels (16:9 aspect ratio)
 * - MOBILE: Fixed base size of 932x430 pixels (iPhone Pro Max landscape - 2.17:1 aspect ratio)
 * - Uniform scaling to maintain aspect ratio (no distortion)
 * - GENERIC SCALING: Automatically scales to fill ANY screen resolution
 * - Content always centered with symmetric letterboxing when aspect ratios differ
 * 
 * Mobile Detection:
 * - Touch + (short landscape bar OR narrow portrait/tablet): min(viewport) <= 820px, or legacy height rules
 * - Mobile uses 932x430 base resolution optimized for phone screens
 * 
 * How it works:
 * - Scale = min(viewportWidth/baseWidth, viewportHeight/baseHeight)
 * - This ensures content always fits without clipping
 * - And fills the maximum possible screen space while maintaining aspect ratio
 * 
 * Desktop Examples (1920x1080 base — also used on WQHD/4K; no larger virtual canvas):
 * - 1280x720:  scale = 0.667x (scales down)
 * - 1920x1080: scale = 1.0x   (native, no scaling)
 * - 2560x1440: scale = 1.333x (uniform upscale to fill 16:9 viewport)
 * - 3840x2160: scale = 2.0x   (uniform upscale to fill 16:9 viewport)
 *
 * isLargeDesktop is still true when viewport is 2560x1440+ (for optional CSS hooks), but
 * baseWidth/baseHeight stay 1920x1080 so the design scale matches 1080p on all large monitors.
 * 
 * Mobile Examples (932x430 base):
 * - iPhone Pro Max (932x430): scale = 1.0x (native)
 * - iPhone 14 Pro (852x393): scale = 0.91x
 * - Samsung Galaxy (915x412): scale = 0.96x
 * 
 * Resolution simulation (dev/testing): add ?simulate=WIDTHxHEIGHT to the URL (e.g. ?simulate=2560x1440)
 * to force the layout to behave as that resolution; the result is scaled to fit your real screen.
 * Returns simulatedResolution: { width, height } when active, null otherwise.
 *
 * IMPORTANT: When applying the scale transform, set transformOrigin: '0 0' on the container so the
 * content scales from the top-left and then translate() centers it. Without this, the default
 * origin (50% 50%) causes only the top-left corner of the layout to be visible when scaled down.
 *
 * @returns {Object} - { scale, offsetX, offsetY, baseWidth, baseHeight, viewportWidth, viewportHeight, isMobile, isLargeDesktop, simulatedResolution }
 */

export { isPhysicalMobileGameViewport } from '../utils/isPhysicalMobileGameViewport';

export function useContainerScale() {
  const [scale, setScale] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1920);
  const [viewportHeight, setViewportHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 1080);
  const [isMobile, setIsMobile] = useState(false);
  const [baseWidth, setBaseWidth] = useState(1920);
  const [baseHeight, setBaseHeight] = useState(1080);

  // Base resolutions
  const DESKTOP_BASE_WIDTH = 1920;
  const DESKTOP_BASE_HEIGHT = 1080;
  /** Viewport threshold only (for `isLargeDesktop` / CSS); not used as layout base size. */
  const LARGE_DESKTOP_VIEWPORT_WIDTH = 2560;
  const LARGE_DESKTOP_VIEWPORT_HEIGHT = 1440;
  const MOBILE_BASE_WIDTH = 932;   // iPhone Pro Max landscape width
  const MOBILE_BASE_HEIGHT = 430;  // iPhone Pro Max landscape height

  const [isLargeDesktop, setIsLargeDesktop] = useState(false);
  const [simulatedResolution, setSimulatedResolution] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const parseSimulateParam = () => {
      if (typeof window === 'undefined') return null;
      const params = new URLSearchParams(window.location.search);
      const sim = params.get('simulate');
      if (!sim) return null;
      const parts = sim.toLowerCase().split('x');
      const w = parseInt(parts[0], 10);
      const h = parseInt(parts[1], 10);
      if (w > 0 && h > 0 && w <= 7680 && h <= 4320) return { width: w, height: h };
      return null;
    };

    const updateScale = () => {
      const realW = window.innerWidth;
      const realH = window.innerHeight;
      const sim = parseSimulateParam();
      setSimulatedResolution(sim || null);

      const vw = sim ? sim.width : realW;
      const vh = sim ? sim.height : realH;

      setViewportWidth(vw);
      setViewportHeight(vh);

      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const shortSide = Math.min(vw, vh);
      const isSmallScreen =
        vh <= 500 || (vw <= 500 && vh <= 932) || shortSide <= 820;
      const detectedMobile = sim ? (hasTouch && isSmallScreen) : isPhysicalMobileGameViewport();

      const detectedLargeDesktop =
        !detectedMobile &&
        vw >= LARGE_DESKTOP_VIEWPORT_WIDTH &&
        vh >= LARGE_DESKTOP_VIEWPORT_HEIGHT;
      setIsLargeDesktop(detectedLargeDesktop);
      setIsMobile(detectedMobile);

      let currentBaseWidth = DESKTOP_BASE_WIDTH;
      let currentBaseHeight = DESKTOP_BASE_HEIGHT;
      if (detectedMobile) {
        currentBaseWidth = MOBILE_BASE_WIDTH;
        currentBaseHeight = MOBILE_BASE_HEIGHT;
      }

      setBaseWidth(currentBaseWidth);
      setBaseHeight(currentBaseHeight);

      const scaleX = realW / currentBaseWidth;
      const scaleY = realH / currentBaseHeight;
      const uniformScale = Math.min(scaleX, scaleY);

      const scaledWidth = currentBaseWidth * uniformScale;
      const scaledHeight = currentBaseHeight * uniformScale;
      const newOffsetX = (realW - scaledWidth) / 2;
      const newOffsetY = (realH - scaledHeight) / 2;

      setScale(uniformScale);
      setOffsetX(newOffsetX);
      setOffsetY(newOffsetY);
    };

    updateScale();

    window.addEventListener('resize', updateScale);
    window.addEventListener('orientationchange', updateScale);

    return () => {
      window.removeEventListener('resize', updateScale);
      window.removeEventListener('orientationchange', updateScale);
    };
  }, [location.search]);

  return {
    scale,
    offsetX,
    offsetY,
    baseWidth,
    baseHeight,
    viewportWidth,
    viewportHeight,
    isMobile,
    isLargeDesktop,
    simulatedResolution
  };
}
